import { TurboModuleRegistry, type TurboModule } from 'react-native';

export interface BleDevice {
  id: string;
  name?: string;
  rssi?: number;
  manufacturerData?: string;
  serviceUUIDs?: string[];
}

export interface BleService {
  uuid: string;
  characteristics: BleCharacteristic[];
}

export interface BleCharacteristic {
  uuid: string;
  properties: string[];
  descriptors?: BleDescriptor[];
}

export interface BleDescriptor {
  uuid: string;
}

export interface BleScanOptions {
  serviceUUIDs?: string[];
  allowDuplicates?: boolean;
  scanMode?: 'lowPower' | 'balanced' | 'lowLatency';
  timeout?: number;
}

export interface BleWriteOptions {
  withResponse?: boolean;
}

export interface BleConnectionState {
  deviceId: string;
  state: 'connecting' | 'connected' | 'disconnecting' | 'disconnected';
}

export interface Spec extends TurboModule {
  // Scanning
  startScan(options?: BleScanOptions): Promise<void>;
  stopScan(): Promise<void>;

  // Connection management
  connect(deviceId: string): Promise<void>;
  disconnect(deviceId: string): Promise<void>;
  isConnected(deviceId: string): Promise<boolean>;
  getConnectedDevices(): Promise<BleDevice[]>;

  // Service discovery
  discoverServices(deviceId: string): Promise<BleService[]>;

  // Characteristic operations
  readCharacteristic(
    deviceId: string,
    serviceUuid: string,
    characteristicUuid: string
  ): Promise<string>;
  writeCharacteristic(
    deviceId: string,
    serviceUuid: string,
    characteristicUuid: string,
    data: string,
    options?: BleWriteOptions
  ): Promise<void>;
  setNotify(
    deviceId: string,
    serviceUuid: string,
    characteristicUuid: string,
    enable: boolean
  ): Promise<void>;

  // Permissions and state
  requestPermissions(): Promise<boolean>;
  isBluetoothEnabled(): Promise<boolean>;
  enableBluetooth(): Promise<void>;

  // Event listeners (these will be handled by the EventEmitter pattern)
  addListener(eventName: string): void;
  removeListeners(count: number): void;

  // Misc
  base64ToHexDash(base64: string): Promise<string>;

  // Infusion control
  startInfusion(deviceId: string): Promise<void>;
  stopInfusion(deviceId: string): Promise<void>;
  setInfusionLevel(deviceId: string, level: number): Promise<void>;

  // User role
  setUserRole(role: string): Promise<void>;
}

export default TurboModuleRegistry.getEnforcing<Spec>('SdkBle');
